import { useEffect, useRef, useState } from 'react'
import {
  useAssistantContext,
  useAssistantSession,
  useClearAssistant,
  useSetAssistantCharacter,
} from '../../domains/assistant/queries'
import { useAssistantChat } from '../../domains/assistant/useAssistantChat'
import { useAssistantDock } from '../../domains/assistant/dockState'
import { describeTool } from '../../domains/assistant/types'
import { Banner, Button, IconButton, Markdown, SectionLabel } from '../../design/components'
import { CharacterContextPicker } from './CharacterContextPicker'
import { DraftReviewModal } from '../drafts/DraftReviewModal'
import styles from './assistant.module.css'

/**
 * The chat. A right-hand drawer on a wide screen, a full sheet on a phone.
 *
 * **One conversation, not one per screen.** The transcript is server state keyed by nothing
 * but the user, so opening the panel from the city screen shows the answer that arrived while
 * the user was on a character sheet.
 *
 * The character is *optional context*, attached to the conversation rather than defining it —
 * so the picker sits in the header next to the title, not in front of the composer as a
 * gate. Nothing about asking a question requires choosing one.
 */
export function AssistantPanel({ onClose }: { onClose: () => void }) {
  const session = useAssistantSession()
  const chat = useAssistantChat()
  const clear = useClearAssistant()
  const setCharacter = useSetAssistantCharacter()

  const requestedCharacterId = useAssistantDock((state) => state.requestedCharacterId)
  const clearRequest = useAssistantDock((state) => state.clearRequest)

  const [question, setQuestion] = useState('')
  const [reviewing, setReviewing] = useState<string | null>(null)
  const [showContext, setShowContext] = useState(false)
  const context = useAssistantContext(showContext && session.data?.contextCharacterId != null)

  const end = useRef<HTMLDivElement>(null)
  const composer = useRef<HTMLTextAreaElement>(null)
  const messages = session.data?.messages ?? []
  const attachedId = session.data?.contextCharacterId ?? null

  // A character handed over by whoever opened the panel — the sheet's "Ask about this
  // character" button. Applied once and then forgotten, so a later plain open does not
  // silently re-attach it. Skipped when it is already the attached one, which is the common
  // case on the second visit and would otherwise be a pointless write.
  useEffect(() => {
    if (requestedCharacterId === null) return
    if (!session.isSuccess) return

    if (requestedCharacterId !== attachedId) setCharacter.mutate(requestedCharacterId)
    clearRequest()
  }, [requestedCharacterId, session.isSuccess, attachedId, setCharacter, clearRequest])

  // Follow the answer as it arrives. `messages.length` alone would not move while a reply
  // streams, which is exactly when the panel most needs to scroll.
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' })
  }, [messages.length, chat.pending?.text])

  // The panel opens because somebody wants to type. Focus the composer rather than making
  // them reach for it — this is a dialog, and the caret is where the dialog's work happens.
  useEffect(() => {
    composer.current?.focus()
  }, [])

  // Escape closes, the convention for anything drawn over the page. Bound to the document so
  // it works while focus is anywhere inside the panel, including the picker.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  function send() {
    chat.ask(question)
    setQuestion('')
  }

  return (
    <>
      {/* The scrim dims the page but does not trap it: the assistant is a companion to what
          is on screen, and a player reading a scene while asking about it is the point. */}
      <div className={styles.scrim} onClick={onClose} aria-hidden="true" />

      <aside
        id="assistant-panel"
        className={styles.panel}
        role="dialog"
        aria-label="Ask Bob the Skull"
      >
        <header className={styles.panelHeader}>
          <div className={styles.panelTitle}>
            <h2 className={styles.panelName}>Bob the Skull</h2>
            <p className={styles.panelSubtitle}>
              {attachedId === null
                ? 'No character attached'
                : (session.data?.contextCharacterName ?? 'Character attached')}
            </p>
          </div>
          <IconButton label="Close the assistant" icon="close" onClick={onClose} />
        </header>

        <div className={styles.contextBar}>
          <CharacterContextPicker
            value={attachedId}
            busy={setCharacter.isPending}
            onChange={(characterId) => setCharacter.mutate(characterId)}
          />
        </div>

        <div className={styles.transcript}>
          {setCharacter.isError && (
            <Banner tone="error">
              That character could not be attached — the assistant only reads characters you own.
            </Banner>
          )}

          {chat.failure !== null && (
            <Banner
              tone="error"
              actions={
                <Button variant="secondary" onClick={chat.dismissFailure}>
                  Dismiss
                </Button>
              }
            >
              {chat.failure}
            </Banner>
          )}

          {chat.fellBack && (
            <Banner tone="info">
              {/* Worth saying: the answer is real and persisted, only the token-by-token
                  effect was lost. The two endpoints persist identically. */}
              That answer arrived in one piece — the streaming connection was unavailable.
            </Banner>
          )}

          {session.isPending && <p className={styles.note}>Loading the conversation…</p>}
          {session.isError && <Banner tone="error">Could not load the conversation.</Banner>}

          {session.isSuccess && messages.length === 0 && chat.pending === null && (
            <p className={styles.note}>
              Ask about the rules, a situation at the table, or anything else. Attach a character
              above and Bob reads their whole sheet — skills, aspects, consequences and items.
            </p>
          )}

          {messages.map((message) => (
            <Turn
              key={message.id}
              role={message.role === 'user' ? 'question' : 'answer'}
              text={message.content ?? ''}
              draftId={message.draftId ?? null}
              onReviewDraft={setReviewing}
            />
          ))}

          {chat.pending !== null && (
            <>
              <Turn role="question" text={chat.pending.question} />
              <Turn role="answer" text={chat.pending.text} streaming tool={chat.pending.tool} />
            </>
          )}

          {showContext && attachedId !== null && (
            <div className={styles.contextDump}>
              <SectionLabel tone="muted" micro>
                What Bob was told
              </SectionLabel>
              {context.isPending ? (
                <p className={styles.note}>Loading…</p>
              ) : context.isError ? (
                <Banner tone="error">Could not load the context.</Banner>
              ) : (
                <p className={styles.contextText}>{context.data.context}</p>
              )}
            </div>
          )}

          <div ref={end} />
        </div>

        <form
          className={styles.composer}
          onSubmit={(event) => {
            event.preventDefault()
            send()
          }}
        >
          <textarea
            ref={composer}
            className={styles.composerBox}
            value={question}
            rows={2}
            aria-label="Your question"
            placeholder="What should I do about the Winter Court?"
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              // Enter sends, Shift+Enter breaks the line — a chat box's own convention, and
              // the form's submit still works for anyone using a keyboard properly.
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                send()
              }
            }}
          />

          <div className={styles.composerActions}>
            {chat.isStreaming ? (
              <Button variant="secondary" onClick={chat.stop}>
                Stop
              </Button>
            ) : (
              <Button type="submit" disabled={question.trim() === ''}>
                Ask
              </Button>
            )}

            <button
              type="button"
              className={styles.textAction}
              disabled={attachedId === null}
              onClick={() => setShowContext((shown) => !shown)}
            >
              {showContext ? 'Hide context' : 'Show context'}
            </button>

            <button
              type="button"
              className={styles.textAction}
              disabled={clear.isPending || messages.length === 0}
              onClick={() => clear.mutate()}
            >
              {clear.isPending ? 'Clearing…' : 'Clear'}
            </button>
          </div>
        </form>
      </aside>

      {/*
        Mounted here rather than in the transcript so the dialog is a sibling of the drawer, not a
        child of a scrolling list — a modal inside an overflow container is a modal that scrolls
        away from the person reading it.
      */}
      {reviewing !== null && (
        <DraftReviewModal draftId={reviewing} onClose={() => setReviewing(null)} />
      )}
    </>
  )
}

/**
 * One turn.
 *
 * The question is a bubble and the answer is not, on purpose. Bob answers at length about a
 * character sheet, and a bubble at 390px turns a paragraph into a column of two-word lines —
 * so the answer runs the full width under its own label, and the short thing the user typed
 * gets the right-aligned bubble that makes the exchange read as a chat.
 *
 * **Only the answer is markdown.** Bob's system prompt is written in markdown and he replies
 * in it, so his turn is rendered. The question is not: the user typed prose into a chat box,
 * and silently turning their asterisks into emphasis would be rewriting what they said.
 */
function Turn({
  role,
  text,
  streaming = false,
  tool = null,
  draftId = null,
  onReviewDraft,
}: {
  role: 'question' | 'answer'
  text: string
  streaming?: boolean
  /** The tool running right now, while the answer has not started. */
  tool?: string | null
  /** A change this reply prepared, if it prepared one. */
  draftId?: string | null
  onReviewDraft?: (draftId: string) => void
}) {
  if (role === 'question') {
    return (
      <div className={styles.question}>
        <span className={styles.turnText}>{text}</span>
      </div>
    )
  }

  return (
    <div className={styles.answer}>
      <SectionLabel tone="arcane" micro>
        Bob
      </SectionLabel>

      {/* While a tool runs there is nothing to show yet, and a bare cursor for six seconds reads
          as a hang. Saying which tool is running costs one line and turns a stall into progress. */}
      {tool !== null && text === '' && (
        <p className={styles.note} role="status">
          {describeTool(tool)}…
        </p>
      )}

      {/* Re-rendered on every token while streaming: half an answer is half-formed markdown,
          and parsing it each time is what makes a list look like a list as it arrives rather
          than snapping into shape at the end. */}
      <Markdown text={text} />
      {streaming && <span className={styles.cursor} aria-hidden="true" />}

      {/* A proposal, not a change. The card is the only route from a conversation to a character,
          and it goes through the review screen — Bob never writes to a sheet. */}
      {draftId != null && onReviewDraft !== undefined && (
        <div className={styles.draftCard}>
          <SectionLabel tone="ember" micro>
            Waiting for you
          </SectionLabel>
          <p className={styles.note}>Bob prepared a change. Nothing has been applied yet.</p>
          <Button variant="outline" onClick={() => onReviewDraft(draftId)}>
            Review it
          </Button>
        </div>
      )}
    </div>
  )
}
